<script setup lang="ts">
import { computed } from 'vue'
import PrimeDialog from 'primevue/dialog'

interface Props {
  modelValue: boolean
  header?: string
  /** Panel width (any CSS length). Default `'400px'`. */
  width?: string
  /**
   * Panel height (any CSS length). Omit to let the content size the panel.
   * Set it when the content cannot size itself — an `<iframe>`, a canvas or a
   * media surface has no intrinsic height, so a content-sized panel collapses
   * to its chrome. The flush cap (`max-height`) still wins on short viewports.
   */
  height?: string
  /** Tap on mask dismisses the dialog. Default `true`. */
  dismissable?: boolean
  /** Show the X close icon in the header. Default `true`. */
  showCloseIcon?: boolean
  /** Modal (with mask + focus trap). Default `true`. */
  modal?: boolean
  /**
   * Strip the panel's own padding and header rule so the slotted content draws
   * the full surface edge to edge — for dialogs that ship their own toolbar /
   * footer chrome (document viewers, media previews). The panel also becomes a
   * height-capped flex column so that content can own its scrolling.
   */
  isFlush?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  width: '400px',
  dismissable: true,
  showCloseIcon: true,
  modal: true,
  isFlush: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'hide'): void
}>()

const pt = computed(() => ({
  root: {
    class: ['app-dialog__panel', props.isFlush ? 'app-dialog__panel--flush' : null],
    style: {
      width: props.width,
      maxWidth: 'calc(100vw - 2rem)',
      ...(props.height ? { height: props.height } : {}),
    },
  },
  header: { class: 'app-dialog__header' },
  content: { class: 'app-dialog__content' },
  mask: { class: 'app-dialog__mask' },
}))

const onHide = () => {
  emit('update:modelValue', false)
  emit('hide')
}
</script>

<template>
  <PrimeDialog
    :visible="modelValue"
    :header="header"
    :dismissable-mask="dismissable"
    :closable="showCloseIcon"
    :modal="modal"
    :pt="pt"
    :block-scroll="true"
    @update:visible="(v: boolean) => !v && onHide()"
  >
    <template v-if="$slots.header" #header>
      <slot name="header" />
    </template>
    <slot />
  </PrimeDialog>
</template>
